import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { sanitizeVariableName } from '@/util/strings';

import { useColumnMappingSuggestions } from '../../hooks/useColumnMappingSuggestions';
import {
  ColumnMapping,
  MatchFieldsModalProps,
  VariableDefinition,
} from '../../types/column-mapping';
import { requiresResidenceState } from '../../utils';

/** Hard limit on debt blocks per subscriber, set by the orchestrator schema. */
export const MAX_DEBT_SLOTS = 20;

/**
 * Matches CSV headers that look like a debt field — with or without a
 * numeric index. Accepts every shape we see in real CSVs:
 *   `debt.amount`, `debts.amount`           — implicit slot 0
 *   `debt.0.amount`, `debts.0.amount`       — explicit dot slot
 *   `debt[1].amount`, `debts[1].amount`     — explicit bracket slot
 *
 * Any field name after the prefix is captured (group 3) so even non-canonical
 * columns (`debt.external_id`, `debts[0].rf_code`) land in the right Debt
 * section. Nested paths like `live_instructions.debts[0].x` are intentionally
 * skipped — they live in Contact details so the user handles them explicitly.
 */
const SLOTTED_HEADER_PATTERN = /^debts?(?:\.(\d+)|\[(\d+)\])?\.(.+)$/i;

/**
 * The two scalar identity targets that can hold at most one primary column,
 * paired with the array-typed target their extras spill into.
 */
const IDENTITY_OVERFLOW: Array<['email' | 'phone', 'alternate_emails' | 'alternate_phones']> = [
  ['email', 'alternate_emails'],
  ['phone', 'alternate_phones'],
];

/**
 * Turns a column mapping into the string value used by the dropdown.
 * - `null`             → column is unmapped (the "Skip" option)
 * - `"<target>"`       → plain target (e.g. `"email"`)
 * - `"<target>:<N>"`   → slotted target pointing at debt index N (e.g. `"debt.amount:1"`)
 */
export const encodeMapping = (mapping: ColumnMapping): string | null => {
  if (!mapping.target) {
    return null;
  }
  if (typeof mapping.debt_index === 'number' && mapping.debt_index > 0) {
    return `${mapping.target}:${mapping.debt_index}`;
  }
  return mapping.target;
};

/**
 * Reverses {@link encodeMapping}: parses the dropdown's string value back
 * into the `{ target, debt_index }` shape stored on a ColumnMapping.
 */
export const decodeValue = (
  encoded: string | null
): Pick<ColumnMapping, 'target' | 'debt_index'> => {
  if (!encoded) {
    return { target: null };
  }
  const colon = encoded.indexOf(':');
  if (colon < 0) {
    return { target: encoded };
  }
  const parsed = parseInt(encoded.slice(colon + 1), 10);
  return { target: encoded.slice(0, colon), debt_index: Number.isNaN(parsed) ? undefined : parsed };
};

/**
 * Reads the debt index out of a CSV header like `debts[0].amount` or
 * `debt.1.currency`. Returns `null` for headers that don't carry one.
 */
export const slotFromHeader = (header: string): number | null => {
  const match = header.match(SLOTTED_HEADER_PATTERN);
  if (!match) {
    return null;
  }
  // Indexless `debt.<field>` / `debts.<field>` → implicit slot 0.
  const raw = match[1] ?? match[2];
  return raw !== undefined ? parseInt(raw, 10) : 0;
};

/**
 * Strips the `debts[N].` / `debt.N.` prefix from a header so the modal shows
 * just the field name (the section heading already tells the user which debt
 * block they're in). Headers without that prefix come back unchanged.
 */
export const formatHeaderForDisplay = (header: string): string => {
  const slotted = header.match(SLOTTED_HEADER_PATTERN);
  return slotted ? slotted[3] : header;
};

/**
 * Keeps the email / phone identity buckets consistent.
 *
 * At most one column can hold the scalar `email` (or `phone`) target — any
 * extras are demoted to `alternate_emails` (or `alternate_phones`). Call
 * this whenever the mappings change so the data the user sees matches what
 * the orchestrator expects.
 *
 * @param preferred Optional override: forces a specific header to be the
 *   primary for `email` and/or `phone` (used by "Make primary" and by
 *   dropdown changes where the user picks `email`/`phone` for a new row).
 */
export const normalizeIdentities = (
  mappings: Record<string, ColumnMapping>,
  csvHeaders: string[],
  preferred?: Partial<Record<'email' | 'phone', string>>
): Record<string, ColumnMapping> => {
  const next: Record<string, ColumnMapping> = { ...mappings };

  for (const [identity, altTarget] of IDENTITY_OVERFLOW) {
    const candidates = csvHeaders.filter(
      (h) => next[h]?.target === identity || next[h]?.target === altTarget
    );

    if (candidates.length === 0) {
      continue;
    }

    if (candidates.length === 1) {
      // Just normalize the primary flag — never auto-promote a lone alternate.
      const only = candidates[0];
      if (next[only]?.target === identity) {
        next[only] = { target: identity, primary: true };
      }
      continue;
    }

    const forced = preferred?.[identity];
    const primary =
      (forced && candidates.includes(forced) ? forced : undefined) ??
      candidates.find((h) => next[h]?.target === identity && next[h]?.primary === true) ??
      candidates.find((h) => next[h]?.target === identity) ??
      candidates[0];

    for (const h of candidates) {
      next[h] = h === primary ? { target: identity, primary: true } : { target: altTarget };
    }
  }

  return next;
};

/**
 * Shape of a single option in the per-row dropdown.
 */
export interface SelectOption {
  value: string | null;
  label: string;
  description?: string;
  required?: boolean;
  conditionalGroup?: string;
}

/**
 * Shape of a single checkbox entry in the "Copy to live_instructions" panel.
 *
 * `path` matches the flat key `transformCsvWithMappings` emits on the
 * subscriber draft — that's what we cross-reference when mirroring values
 * under `context.live_instructions.*`.
 */
export interface LiveInstructionOption {
  /** Emit path (e.g. `email`, `debt.0.amount`, `alternate_phones`, `promo_code`). */
  path: string;
  /** Human-readable label shown to the user. */
  label: string;
  /** Optional debt-slot label suffix, e.g. "Debt #1". */
  slotLabel?: string;
  /** Mapped NBA variable vs. fall-through CSV column. */
  origin: 'nba' | 'csv';
}

/**
 * A required system field with no CSV column matched to it. Slot-aware: a
 * slotted debt field (`debt.amount`) yields one entry per debt block that is
 * still unmatched, so removing the amount from debt #2 alone surfaces here
 * even when debt #1 is fully mapped.
 */
export interface MissingRequiredField {
  /** Variable name, e.g. `email`, `debt.amount`. */
  name: string;
  /** Human-readable label. */
  label: string;
  /** Debt block this requirement belongs to (0 for contact / non-slotted). */
  debtIndex: number;
  /** Slot label suffix shown when there's more than one debt block. */
  slotLabel?: string;
}

/**
 * Section id passed around the modal: either Contact details, or one of the
 * debt sections (one per debt block).
 */
export type SectionId = 'contact' | `debt-${number}`;

/**
 * Order non-slotted variables for the Contact section in the reverse view:
 * required vars first, then conditional contact vars, then optional, with
 * `multi`-type vars (alternate_*) trailing each group.
 */
const orderContactVariables = (
  variables: VariableDefinition[],
  dynamicallyRequired: Set<string>
): VariableDefinition[] => {
  const isReq = (v: VariableDefinition) => v.required || dynamicallyRequired.has(v.name);
  const isContactGroup = (v: VariableDefinition) => v.conditionalGroup === 'contact';
  const bucket = (v: VariableDefinition) => {
    if (isReq(v)) {
      return v.multi ? 1 : 0;
    }
    if (isContactGroup(v)) {
      return 2;
    }
    return v.multi ? 4 : 3;
  };
  return [...variables].filter((v) => !v.slotted).sort((a, b) => bucket(a) - bucket(b));
};

/**
 * Order slotted variables for a Debt section in the reverse view: required
 * vars first, then optional. Used once per slot.
 */
const orderDebtVariables = (variables: VariableDefinition[]): VariableDefinition[] =>
  [...variables]
    .filter((v) => v.slotted)
    .sort((a, b) => Number(Boolean(b.required)) - Number(Boolean(a.required)));

/**
 * Sanitize a CSV header into the dotted emit path `transformCsvWithMappings`
 * uses as a fallback when no mapping target is provided. Kept in lockstep
 * with the inline fallback in `transformCsvWithMappings` so the live-
 * instructions panel labels match what actually lands on the subscriber.
 */
const sanitizeHeaderToEmitPath = (header: string): string =>
  header
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .map((seg) => (/^\d+$/.test(seg) ? seg : sanitizeVariableName(seg).toLowerCase()))
    .filter((seg) => seg.length > 0)
    .join('.');

export interface UseMatchFieldsModalResult {
  // ----- raw state -----
  /** Current mapping for each CSV header. Read by `renderRow` to encode for the dropdown. */
  mappings: Record<string, ColumnMapping>;
  /** True while the auto-suggest call is in flight (used to disable the Confirm button). */
  isSuggesting: boolean;

  // ----- derived data -----
  /** Number of debt sections to render — at least 1, capped at {@link MAX_DEBT_SLOTS}. */
  debtSlotCount: number;
  /**
   * NBA variables grouped by section. Contact gets non-slotted variables in a
   * stable presentation order; each debt-N gets the full set of slotted vars
   * (one row per slot per variable). Required vars surface first.
   */
  variablesBySection: Record<string, VariableDefinition[]>;
  /**
   * True when two or more CSV columns are both mapped to the scalar `email`
   * target with no alternate assignment — i.e. the user still needs to pick
   * which one is primary. Stays false in the resolved 1 primary + N alternates
   * case, since the dropdown labels already convey that.
   */
  hasMultipleEmails: boolean;
  /** Same as {@link hasMultipleEmails} for `phone`. */
  hasMultiplePhones: boolean;
  /**
   * Required system fields that no CSV column currently maps to, one entry per
   * unmatched (field, debt slot). The contact group (`email`/`phone`) is a
   * single "Email or phone" requirement — satisfied as long as at least one
   * column maps to either primary or alternate variant — but when nothing is
   * mapped BOTH rows are reported so neither field looks individually optional.
   * Slotted debt fields are reported per debt block, so an amount removed from
   * one debt block surfaces even when the others are complete.
   */
  missingRequiredFields: MissingRequiredField[];

  // ----- handlers -----
  /**
   * Apply the user picking (or clearing) a CSV column for a single-select NBA
   * variable. Clears any header that previously pointed at this `{target,
   * debt_index}` slot. Pass `csvHeader = null` to leave the slot empty.
   */
  handleVariableMappingChange: (
    target: string,
    debtIndex: number,
    csvHeader: string | null
  ) => void;
  /**
   * Toggle a CSV column for a multi-select NBA variable (alternate_emails /
   * alternate_phones). Adding flips an unmapped header to point at the multi
   * target; removing clears it back to `{ target: null }`.
   */
  handleMultiVariableToggle: (target: string, csvHeader: string, checked: boolean) => void;
  /** Confirm the current mappings + live-instruction selections and close the modal. */
  handleConfirm: () => void;

  // ----- per-section helpers -----
  /** CSV column dropdown options for a given NBA variable row in the reverse view. */
  csvOptionsForVariable: (target: string, debtIndex: number) => SelectOption[];
  /**
   * Returns the CSV header(s) currently mapped to this `{target, debt_index}`.
   * For single-select rows the array has length 0 or 1; for multi-select rows
   * (alternate_*), it lists every header pointing at the bucket.
   */
  findHeadersForVariable: (target: string, debtIndex: number) => string[];
  /** First non-empty sample value for a header, or `null` if none of the sample rows had one. */
  getPreviewValue: (header: string) => string | null;

  // ----- live-instructions panel -----
  /** Checkbox options shown under the "Copy to live_instructions" panel. */
  liveInstructionOptions: LiveInstructionOption[];
  /** Selected emit paths (subset of `liveInstructionOptions[].path`). */
  liveInstructionTargets: Set<string>;
  /** Flip an individual emit path on/off. */
  toggleLiveInstructionTarget: (path: string) => void;
  /** Bulk-select / clear every option in `liveInstructionOptions`. */
  setAllLiveInstructionTargets: (checked: boolean) => void;
}

/**
 * Owns every piece of state and derived data the Match Fields modal needs.
 *
 * The modal itself is just a renderer over the values this returns: column
 * mappings, the per-section grouping of headers, primary email/phone
 * detection, the dropdown options for each section, and the handlers that
 * mutate state. Pure helpers (encode/decode, normalizeIdentities, etc.) are
 * exported alongside so they can be unit-tested without a React tree.
 */
export const useMatchFieldsModal = ({
  open,
  onOpenChange,
  csvHeaders,
  sampleRows,
  availableVariables,
  initialMappings,
  skipAutoSuggest = false,
  extraRequiredFields,
  initialLiveInstructionTargets,
  onConfirm,
}: MatchFieldsModalProps): UseMatchFieldsModalResult => {
  const { t } = useTranslation();
  const { suggestMappingsAsync } = useColumnMappingSuggestions();

  // Identity of the CSV currently loaded. Changes when a new file replaces the
  // old one — even while the modal stays mounted/open (delete-then-reupload).
  // Both the auto-suggest gate and the mappings reset key off this so a second
  // upload isn't stuck showing the first file's (empty) rows.
  const csvSignature = useMemo(() => csvHeaders.join(''), [csvHeaders]);

  // Signature we last fired auto-suggest for (null = not yet, or reset on close).
  // Replaces the old "once per open" boolean so a header change re-fires.
  const lastSuggestedSignature = useRef<string | null>(null);
  // Signature we last seeded the mappings state for — guards the re-seed effect.
  const lastSeededSignature = useRef<string | null>(csvSignature);
  // Gates the response handler so a late reply doesn't overwrite a closed modal.
  const shouldAcceptResponse = useRef(false);

  const [isSuggesting, setIsSuggesting] = useState(false);

  const [mappings, setMappings] = useState<Record<string, ColumnMapping>>(() => {
    if (initialMappings) {
      return initialMappings;
    }
    const initial: Record<string, ColumnMapping> = {};
    csvHeaders.forEach((header) => {
      initial[header] = { target: null };
    });
    return initial;
  });

  const [liveInstructionTargets, setLiveInstructionTargets] = useState<Set<string>>(
    () => new Set(initialLiveInstructionTargets ?? [])
  );

  // Resync local state when the modal reopens with a different initial set
  // (e.g. user closes, edits the campaign, reopens). We only do this on open
  // transitions so user picks aren't reset mid-session.
  useEffect(() => {
    if (open) {
      setLiveInstructionTargets(new Set(initialLiveInstructionTargets ?? []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset the auto-suggest gate whenever the modal opens or closes.
  useEffect(() => {
    if (open) {
      shouldAcceptResponse.current = true;
    } else {
      shouldAcceptResponse.current = false;
      lastSuggestedSignature.current = null;
      setIsSuggesting(false);
    }
  }, [open]);

  // Re-seed the mappings to all-unmapped when a *new* CSV (different headers)
  // arrives while the modal is open and the caller didn't hand us explicit
  // mappings. Without this, replacing the file in place leaves the previous
  // file's mappings keyed by stale headers, so the rows render empty.
  useEffect(() => {
    if (!open || initialMappings) {
      return;
    }
    if (lastSeededSignature.current === csvSignature) {
      return;
    }
    lastSeededSignature.current = csvSignature;
    const seeded: Record<string, ColumnMapping> = {};
    csvHeaders.forEach((header) => {
      seeded[header] = { target: null };
    });
    setMappings(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMappings, csvSignature]);

  // Fire auto-suggest once per CSV — for fresh uploads (no `initialMappings`)
  // when the caller hasn't opted out. Keyed on `csvSignature` so swapping the
  // file re-fires for the new headers instead of running only once per open.
  useEffect(() => {
    if (
      open &&
      !initialMappings &&
      !skipAutoSuggest &&
      lastSuggestedSignature.current !== csvSignature
    ) {
      lastSuggestedSignature.current = csvSignature;
      setIsSuggesting(true);
      suggestMappingsAsync({
        headers: csvHeaders,
        sample_rows: sampleRows.slice(0, 3),
        available_variables: availableVariables,
      })
        .then((response) => {
          if (shouldAcceptResponse.current) {
            setMappings(normalizeIdentities(response.mappings, csvHeaders));
          }
        })
        .catch((error) => {
          console.error('Failed to get column mapping suggestions:', error);
        })
        .finally(() => {
          if (shouldAcceptResponse.current) {
            setIsSuggesting(false);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialMappings, skipAutoSuggest, suggestMappingsAsync, csvSignature]);

  /** Names of every variable that supports multiple debt blocks (debt.amount, …). */
  const slottedTargets = useMemo(
    () => new Set(availableVariables.filter((v) => v.slotted).map((v) => v.name)),
    [availableVariables]
  );

  // How many debt sections to render. Starts at 1 and grows whenever a
  // mapping (or a CSV header pattern) references a higher debt index.
  const debtSlotCount = useMemo(() => {
    let usedMax = 0;
    for (const header of csvHeaders) {
      const m = mappings[header];
      if (m?.target && slottedTargets.has(m.target)) {
        usedMax = Math.max(usedMax, (m.debt_index ?? 0) + 1);
      }
      const headerSlot = slotFromHeader(header);
      if (headerSlot !== null) {
        usedMax = Math.max(usedMax, headerSlot + 1);
      }
    }
    return Math.min(MAX_DEBT_SLOTS, Math.max(1, usedMax));
  }, [mappings, csvHeaders, slottedTargets]);

  // We only warn when two or more columns are mapped to the SCALAR identity
  // target with no alternate assignment — that's the "unresolved" case where
  // the user still has to pick a primary. Once one is primary and the rest
  // are alternates, the dropdown labels already convey the split.
  const primaryEmailCount = csvHeaders.filter((h) => mappings[h]?.target === 'email').length;
  const primaryPhoneCount = csvHeaders.filter((h) => mappings[h]?.target === 'phone').length;
  const hasMultipleEmails = primaryEmailCount > 1;
  const hasMultiplePhones = primaryPhoneCount > 1;

  // Names that are required for this audience even if their static `required`
  // flag is false. Combines the caller-supplied `extraRequiredFields` with the
  // self-detected `residence_state`-when-US/BR rule (mirrors the orchestrator's
  // FDCPA logic from `scanPaymentRecoveryRequirements`). Lifted out of
  // `missingRequiredFields` so the option builder can use the same set to mark
  // dropdown labels as required.
  const effectiveExtraRequiredSet = useMemo(() => {
    const set = new Set(extraRequiredFields ?? []);
    const countryHeader =
      csvHeaders.find((h) => mappings[h]?.target === 'country_code') ??
      csvHeaders.find((h) => h.toLowerCase() === 'country_code');
    if (countryHeader) {
      const idx = csvHeaders.indexOf(countryHeader);
      const triggersResidence = sampleRows.some((row) => requiresResidenceState(row[idx]));
      if (triggersResidence) {
        set.add('residence_state');
      }
    }
    return set;
  }, [extraRequiredFields, mappings, csvHeaders, sampleRows]);

  // Reverse-view: enumerate NBA variables per section instead of CSV headers.
  // Contact gets non-slotted variables once; each debt section gets the full
  // slotted set (one row per slot per variable).
  const variablesBySection = useMemo(() => {
    const sectionsMap: Record<string, VariableDefinition[]> = {
      contact: orderContactVariables(availableVariables, effectiveExtraRequiredSet),
    };
    const debtVars = orderDebtVariables(availableVariables);
    for (let i = 0; i < debtSlotCount; i++) {
      sectionsMap[`debt-${i}`] = debtVars;
    }
    return sectionsMap;
  }, [availableVariables, effectiveExtraRequiredSet, debtSlotCount]);

  // Required system fields with no CSV column mapped to them, one entry per
  // unmatched (field, debt slot). Mirrors the section/slot layout the modal
  // renders so the per-row highlight and the summary card agree.
  //  - Contact group (email/phone): a single "Email or phone" requirement, but
  //    when neither (nor an alternate) is mapped we report BOTH rows — flagging
  //    only the first made the other field look optional.
  //  - Slotted debt fields: checked per debt block, so removing the amount from
  //    one block surfaces even when the others are fully mapped.
  const missingRequiredFields = useMemo(() => {
    const extraRequiredSet = effectiveExtraRequiredSet;

    const mappedTargets = new Set(
      Object.values(mappings)
        .map((m) => m.target)
        .filter((t): t is string => Boolean(t))
    );
    const hasAnyContact =
      mappedTargets.has('email') ||
      mappedTargets.has('phone') ||
      mappedTargets.has('alternate_emails') ||
      mappedTargets.has('alternate_phones');

    // Is any column mapped to (target, slot)? Mirrors `findHeadersForVariable`.
    const isMapped = (name: string, debtIndex: number): boolean =>
      csvHeaders.some((h) => {
        const m = mappings[h];
        return !!m?.target && m.target === name && (m.debt_index ?? 0) === debtIndex;
      });
    const isRequired = (v: VariableDefinition): boolean =>
      Boolean(v.required) || extraRequiredSet.has(v.name);

    const missing: MissingRequiredField[] = [];

    // Contact section (slot 0).
    for (const v of variablesBySection.contact ?? []) {
      // extraRequiredFields wins over the contact short-circuit so a variable
      // like residence_state (conditionally required by country) surfaces
      // independently even if some past version tagged it with a group.
      if (v.conditionalGroup === 'contact' && !extraRequiredSet.has(v.name)) {
        if (!hasAnyContact) {
          missing.push({ name: v.name, label: v.label || v.name, debtIndex: 0 });
        }
        continue;
      }
      if (isRequired(v) && !isMapped(v.name, 0)) {
        missing.push({ name: v.name, label: v.label || v.name, debtIndex: 0 });
      }
    }

    // Debt sections — one row per (required field, slot).
    for (let i = 0; i < debtSlotCount; i++) {
      const slotLabel =
        debtSlotCount > 1 ? t('campaigns.match_fields.debt_details_n', { 0: i + 1 }) : undefined;
      for (const v of variablesBySection[`debt-${i}`] ?? []) {
        if (isRequired(v) && !isMapped(v.name, i)) {
          missing.push({ name: v.name, label: v.label || v.name, debtIndex: i, slotLabel });
        }
      }
    }

    return missing;
  }, [mappings, csvHeaders, variablesBySection, effectiveExtraRequiredSet, debtSlotCount, t]);

  /**
   * Single-select reverse handler. Clears any header that previously occupied
   * `{target, debt_index}`, then assigns the new header (if provided). Email
   * and phone selections set `primary: true` so the existing identity-overflow
   * normalization (`normalizeIdentities`) can demote any leftover columns into
   * `alternate_*` buckets.
   */
  const handleVariableMappingChange = useCallback(
    (target: string, debtIndex: number, csvHeader: string | null) => {
      setMappings((prev) => {
        const next: Record<string, ColumnMapping> = { ...prev };

        // Clear any header currently pointing at this exact slot.
        for (const h of csvHeaders) {
          const m = next[h];
          if (m?.target === target && (m.debt_index ?? 0) === debtIndex) {
            next[h] = { target: null };
          }
        }

        if (csvHeader) {
          const assignment: ColumnMapping = { target };
          if (debtIndex > 0) {
            assignment.debt_index = debtIndex;
          }
          if (target === 'email' || target === 'phone') {
            assignment.primary = true;
          }
          next[csvHeader] = assignment;
        }

        const preferred =
          csvHeader && (target === 'email' || target === 'phone')
            ? { [target]: csvHeader }
            : undefined;
        return normalizeIdentities(next, csvHeaders, preferred);
      });
    },
    [csvHeaders]
  );

  /**
   * Multi-select reverse handler for `multi: true` targets (alternate_*).
   * Adds or removes a single CSV header from the bucket without touching
   * sibling headers already pointing at the same target.
   */
  const handleMultiVariableToggle = useCallback(
    (target: string, csvHeader: string, checked: boolean) => {
      setMappings((prev) => {
        const next: Record<string, ColumnMapping> = { ...prev };
        if (checked) {
          next[csvHeader] = { target };
        } else if (next[csvHeader]?.target === target) {
          next[csvHeader] = { target: null };
        }
        // Don't run normalizeIdentities here — for `multi` buckets
        // (alternate_emails / alternate_phones) the user is explicitly
        // populating the bucket, and the function's "promote a lone
        // alternate to primary" rule would clobber their intent.
        return next;
      });
    },
    []
  );

  const findHeadersForVariable = useCallback(
    (target: string, debtIndex: number): string[] => {
      const result: string[] = [];
      for (const h of csvHeaders) {
        const m = mappings[h];
        if (!m?.target || m.target !== target) {
          continue;
        }
        if ((m.debt_index ?? 0) !== debtIndex) {
          continue;
        }
        result.push(h);
      }
      return result;
    },
    [csvHeaders, mappings]
  );

  /**
   * CSV columns offered for a given NBA variable row. Any header already
   * mapped to a different target is omitted so the user can't double-book
   * a column; headers mapped to this same target are kept so the dropdown
   * can reflect the current selection.
   */
  const csvOptionsForVariable = useCallback(
    (target: string, debtIndex: number): SelectOption[] => {
      return (
        csvHeaders
          .filter((h) => {
            const m = mappings[h];
            if (!m?.target) {
              return true;
            }
            if (m.target === target && (m.debt_index ?? 0) === debtIndex) {
              return true;
            }
            return false;
          })
          .slice()
          .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
          // Show the raw CSV header so users can tell `debts[0].amount`
          // apart from `debts[1].amount` — stripping the `debts[N].`
          // prefix hides exactly the info that disambiguates them.
          .map((h) => ({ value: h, label: h, description: h }))
      );
    },
    [csvHeaders, mappings]
  );

  const getPreviewValue = useCallback(
    (header: string): string | null => {
      const headerIdx = csvHeaders.indexOf(header);
      if (headerIdx < 0) {
        return null;
      }
      for (const row of sampleRows) {
        const cell = row[headerIdx]?.trim();
        if (cell) {
          return cell;
        }
      }
      return null;
    },
    [csvHeaders, sampleRows]
  );

  // Build the list of subscriber-context paths the user might want to mirror
  // under live_instructions. Two origins:
  //  - 'nba' rows: one per mapped NBA target, expanded by debt slot for
  //    slotted vars. Multi targets (alternate_*) list once.
  //  - 'csv' rows: one per CSV header that didn't pick any target — these
  //    fall through to `context.<sanitized_header>` and the support team
  //    typically wants those visible to the LLM.
  const liveInstructionOptions = useMemo<LiveInstructionOption[]>(() => {
    const options: LiveInstructionOption[] = [];
    const seenPaths = new Set<string>();
    const labelByName = new Map(availableVariables.map((v) => [v.name, v.label || v.name]));
    const slottedSet = new Set(availableVariables.filter((v) => v.slotted).map((v) => v.name));
    const multiSet = new Set(availableVariables.filter((v) => v.multi).map((v) => v.name));

    const addOption = (opt: LiveInstructionOption) => {
      if (seenPaths.has(opt.path)) {
        return;
      }
      seenPaths.add(opt.path);
      options.push(opt);
    };

    for (const h of csvHeaders) {
      const m = mappings[h];
      const target = m?.target;
      if (!target) {
        continue;
      }
      const label = labelByName.get(target) ?? target;

      if (slottedSet.has(target)) {
        const slot = m.debt_index ?? 0;
        const dotIdx = target.indexOf('.');
        const field = dotIdx > 0 ? target.slice(dotIdx + 1) : target;
        const path = `debt.${slot}.${field}`;
        const slotLabel =
          debtSlotCount > 1
            ? t('campaigns.match_fields.debt_details_n', { 0: slot + 1 })
            : t('campaigns.match_fields.debt_details');
        addOption({ path, label, slotLabel, origin: 'nba' });
        continue;
      }

      if (multiSet.has(target)) {
        addOption({ path: target, label, origin: 'nba' });
        continue;
      }

      // Identity overflow buckets: when extras spill to alternate_*, the
      // resolved emit path is the bucket name. Demoted entries still surface
      // under the bucket option above, so no extra row needed here.
      if (target === 'alternate_emails' || target === 'alternate_phones') {
        addOption({ path: target, label, origin: 'nba' });
        continue;
      }

      addOption({ path: target, label, origin: 'nba' });
    }

    // Unmapped CSV headers — they will fall through with the sanitized path.
    for (const h of csvHeaders) {
      if (mappings[h]?.target) {
        continue;
      }
      const path = sanitizeHeaderToEmitPath(h);
      if (!path) {
        continue;
      }
      addOption({ path, label: h, origin: 'csv' });
    }

    return options;
  }, [csvHeaders, mappings, availableVariables, debtSlotCount, t]);

  // Drop any selection paths that no longer correspond to an available option
  // (e.g. the user re-mapped a CSV col after marking it for live_instructions).
  // Skip while there are no options yet: on an auto-suggest reopen the options
  // list is empty until mappings resolve async, and pruning then would wipe the
  // selection just restored from `initialLiveInstructionTargets`.
  useEffect(() => {
    if (liveInstructionOptions.length === 0) {
      return;
    }
    setLiveInstructionTargets((prev) => {
      const validPaths = new Set(liveInstructionOptions.map((o) => o.path));
      let changed = false;
      const next = new Set<string>();
      for (const p of prev) {
        if (validPaths.has(p)) {
          next.add(p);
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [liveInstructionOptions]);

  const toggleLiveInstructionTarget = useCallback((path: string) => {
    setLiveInstructionTargets((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const setAllLiveInstructionTargets = useCallback(
    (checked: boolean) => {
      setLiveInstructionTargets(
        checked ? new Set(liveInstructionOptions.map((o) => o.path)) : new Set()
      );
    },
    [liveInstructionOptions]
  );

  const handleConfirm = useCallback(() => {
    onConfirm(mappings, Array.from(liveInstructionTargets));
    onOpenChange(false);
  }, [onConfirm, mappings, liveInstructionTargets, onOpenChange]);

  return {
    mappings,
    isSuggesting,
    debtSlotCount,
    variablesBySection,
    hasMultipleEmails,
    hasMultiplePhones,
    missingRequiredFields,
    handleVariableMappingChange,
    handleMultiVariableToggle,
    handleConfirm,
    csvOptionsForVariable,
    findHeadersForVariable,
    getPreviewValue,
    liveInstructionOptions,
    liveInstructionTargets,
    toggleLiveInstructionTarget,
    setAllLiveInstructionTargets,
  };
};
